import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Download, MessageCircle, Facebook } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useT } from '../contexts/LanguageContext';

export const DEFAULT_THANK_YOU =
  'Dear soul, your offering is not merely a gift of wealth — it is a seed of seva planted in the sacred soil of Sanatana Dharma. Every contribution helps a seeker find stillness, keeps the ancient mantras alive, and carries the wisdom of the Rishis to generations yet to come.\n\nMay the blessings of the Rishis walk beside you, and may your home be filled with peace, health and abundance.';

const readDonation = () => {
  try { return JSON.parse(sessionStorage.getItem('rf_last_donation') || 'null'); } catch { return null; }
};

export default function DonationThankYou() {
  usePageTitle('Thank You');
  const settings = useSiteSettings();
  const tr = useT();
  const donation = readDonation();
  const message = settings.donation_thank_you_message || DEFAULT_THANK_YOU;
  const siteUrl = window.location.origin;
  const shareText = `${tr('I just supported the Rishidev Foundation in preserving the timeless wisdom of Sanatana Dharma. Join me:')} ${siteUrl}`;
  const shares = [
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(shareText)}`, cls: 'bg-[#25D366] hover:bg-[#1ebe5b]' },
    { id: 'facebook', label: 'Facebook', icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}`, cls: 'bg-[#1877F2] hover:bg-[#0f66d8]' },
  ];

  return (
    <div className="min-h-screen pt-20 bg-cream" data-testid="donation-thank-you-page">
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="w-20 h-20 mx-auto bg-gradient-to-br from-saffron-500 to-gold-500 rounded-full flex items-center justify-center mb-8 shadow-lg">
            <Heart className="text-white" size={34} fill="white" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="text-4xl sm:text-5xl font-heading text-charcoal-800 text-center mb-4">
            {tr('Thank you')}{donation?.name ? `, ${donation.name}` : ''} 🙏
          </motion.h1>
          {donation?.amount && (
            <p className="text-center text-charcoal-600 mb-10" data-testid="thank-you-amount">
              {tr('Your donation of')} <span className="font-semibold text-saffron-700">₹{Number(donation.amount).toLocaleString('en-IN')}</span> {tr('has been received.')}
            </p>
          )}
          <motion.blockquote initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="relative bg-white rounded-2xl border border-saffron-100 shadow-[0_8px_30px_rgba(44,30,22,0.06)] p-8 md:p-12" data-testid="guruji-message">
            <span className="absolute -top-6 left-8 text-7xl font-heading text-saffron-500 leading-none">“</span>
            <div className="text-xs uppercase tracking-[0.2em] text-saffron-600 font-semibold mb-4">{tr('A message from Guruji')}</div>
            {message.split('\n').filter(Boolean).map((p, i) => (
              <p key={i} className="font-heading text-lg md:text-xl text-charcoal-700 leading-relaxed mb-4 italic">{tr(p)}</p>
            ))}
            <footer className="mt-6 text-right font-heading text-charcoal-800">— {tr('Guruji Risheeshwar Narendranji')}</footer>
          </motion.blockquote>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10 flex flex-wrap justify-center gap-3">
            {donation?.receipt_url && (
              <a href={donation.receipt_url} target="_blank" rel="noopener noreferrer" data-testid="download-receipt-button" className="inline-flex items-center gap-2 bg-saffron-600 text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-saffron-500 transition-colors shadow">
                <Download size={18} /> {tr('Download PDF Receipt')}
              </a>
            )}
            <Link to=".." relative="path" data-testid="donate-again-button" className="inline-flex items-center gap-2 bg-white border border-charcoal-200 text-charcoal-700 rounded-full px-6 py-3 text-sm font-medium hover:bg-charcoal-50 transition-colors">
              {tr('Make Another Donation')}
            </Link>
          </motion.div>

          <div className="mt-14 text-center">
            <h2 className="text-base md:text-lg font-heading text-charcoal-800 mb-2">{tr('Share the light')}</h2>
            <p className="text-sm text-charcoal-500 mb-5">{tr('Invite friends and family to discover the foundation.')}</p>
            <div className="flex justify-center gap-3">
              {shares.map(({ id, label, icon: Icon, href, cls }) => (
                <a key={id} href={href} target="_blank" rel="noopener noreferrer" data-testid={`share-${id}`} className={`inline-flex items-center gap-2 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors ${cls}`}>
                  <Icon size={18} /> {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
